import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mutate as globalMutate } from "swr";
import * as Sentry from "@sentry/react";
import { useApiClient } from "@/hooks/useApiClient";
import { postOauthToken } from "@/features/salesforceIntegration/api/postOauthToken";
import { SALESFORCE_CONNECTION_ENDPOINT } from "@/features/salesforceIntegration/api/getConnection";

export type SalesforceOauthCallbackState = "processing" | "error";

export interface UseSalesforceOauthCallbackResult {
  state: SalesforceOauthCallbackState;
  errorMessage: string | null;
}

const MISSING_PARAMS_MESSAGE =
  "認可情報を取得できませんでした。設定画面からやり直してください。";
const REQUEST_FAILED_MESSAGE =
  "Salesforceとの連携に失敗しました。時間をおいて再度お試しください。";

const CALLBACK_PATH = "/auth/callback/on-salesforce";
const SUCCESS_PATH = "/auth/success/on-salesforce";

/**
 * Salesforce OAuthコールバック画面のロジック。
 * `code`と`state`をバックエンドへ渡してトークン交換を行い、成功したら完了画面へ遷移する。
 * `code_verifier`の生成やトークン交換自体はバックエンドの責務であり、
 * フロントエンドは認可コードの中継のみを担う（実装計画書6.1節）。
 */
export const useSalesforceOauthCallback =
  (): UseSalesforceOauthCallbackResult => {
    const { apiClient } = useApiClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [state, setState] =
      useState<SalesforceOauthCallbackState>("processing");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const hasRequestedRef = useRef(false);

    useEffect(() => {
      if (hasRequestedRef.current) {
        return;
      }
      hasRequestedRef.current = true;

      const code = searchParams.get("code");
      const oauthState = searchParams.get("state");

      // 認可コードをブラウザ履歴（戻る操作・再読み込み）に残さない
      window.history.replaceState({}, document.title, CALLBACK_PATH);

      if (!code || !oauthState) {
        setErrorMessage(MISSING_PARAMS_MESSAGE);
        setState("error");
        return;
      }

      postOauthToken(apiClient, { code, state: oauthState })
        .then((connection) => {
          // 接続状態のSWRキャッシュへ即時反映し、完了画面で空表示を挟まず最新状態を出す。
          // SWRのマウント時再検証は別途走るため、フェッチを止めるものではない。
          void globalMutate(SALESFORCE_CONNECTION_ENDPOINT, connection, false);
          navigate(SUCCESS_PATH, { replace: true });
        })
        .catch((error) => {
          Sentry.captureException(error);
          setErrorMessage(REQUEST_FAILED_MESSAGE);
          setState("error");
        });
    }, [apiClient, navigate, searchParams]);

    return { state, errorMessage };
  };
